import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, UserCog } from "lucide-react";

const EMPTY = { name: "", role: "", phone: "", hourly_rate: 0 };

export default function Employees() {
  const { t } = useLang();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/employees", { ...form, hourly_rate: Number(form.hourly_rate) || 0 });
      toast.success("Empleado agregado"); setForm(EMPTY); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/employees/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{t("dash_employees")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-sm bg-primary hover:bg-orange-600" data-testid="add-employee-btn"><Plus className="mr-1 h-4 w-4" />{t("add")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("dash_employees")}</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3" data-testid="employee-form">
              <div><Label className="text-xs font-bold uppercase text-slate-500">{t("name")}</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1 rounded-sm" data-testid="employee-name-input" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500">Puesto</Label><Input value={form.role} onChange={(e) => set("role", e.target.value)} className="mt-1 rounded-sm" data-testid="employee-role-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-bold uppercase text-slate-500">Teléfono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs font-bold uppercase text-slate-500">$/hora</Label><Input type="number" value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} className="mt-1 rounded-sm" /></div>
              </div>
              <Button type="submit" className="w-full rounded-sm bg-primary hover:bg-orange-600" data-testid="employee-save-btn">{t("save")}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="employees-grid">
        {employees.map((e) => (
          <div key={e.id} className="flex items-center gap-4 rounded-sm border border-slate-200 bg-white p-5" data-testid={`employee-card-${e.id}`}>
            <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-slate-100 text-secondary"><UserCog className="h-5 w-5" /></span>
            <div className="flex-1">
              <h3 className="font-head font-bold text-secondary">{e.name}</h3>
              <p className="text-sm text-slate-500">{e.role || "—"}{e.hourly_rate > 0 ? ` · $${e.hourly_rate}/hr` : ""}</p>
            </div>
            <button onClick={() => remove(e.id)} className="text-slate-300 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {employees.length === 0 && <p className="col-span-full py-10 text-center text-slate-400">—</p>}
      </div>
    </div>
  );
}
